// traceArtifacts.js — the published ARTIFACTS a scan window's agents shipped,
// as first-class objects (the artifact analog of the Files tree: artifact =
// file, deploy = write). Feeds the X-ray Artifacts tab.
//
// Detection keys on the Artifact TOOL, not on any skill: every Artifact
// tool_use is a publish, whether artifact-design loaded first or nothing did.
// Skills enter only as a LABEL — the run that owned the publish (positional,
// same attribution rule as the skills aggregation) becomes "under <skill>".
// The extractor is deliberately lean: unlike traceSkillRuns' full-signal walk
// (per-command verbs, objects, forge classification), this one collects just
// publishes and skill invocations — the two things the tab consumes.
//
//   artifact — one published page, identified by its URL when the result
//              carried one (stable across sessions), else session+path (a
//              scratchpad path is only meaningful within its session).
//   deploys  — every publish of it, chronological, actor-attributed.
//
// Reconstruction of the page itself happens component-side: the backing
// path + deploy sessions select the same-session Write/Edit ops, and
// reconstructFile replays them — identical mechanic to the Files tab.

import { normalizeSkillName, commandInvocationsOf } from "./traceSkillRuns.js";
import { collectWindowOps, reconstructFile } from "./traceFileTree.js";
import { shellCommandOf, splitStatements, stripHeredocs, unquoted } from "./traceForgeOps.js";

// The window file ops that back an artifact, for reconstruction. Primary
// match is per-deploy: each deploy's OWN (path, session) pair — a
// URL-identified artifact republished from different files across sessions
// follows every backing file, chronologically. The fallback is PER PATH, not
// all-or-nothing: a backing path with no strict match anywhere (the
// publishing session staged the file through the shell) resolves by path
// window-wide, while strictly-matched paths keep their session pinning —
// scratchpad paths are session-scoped strings, so cross-session collisions
// are not a real risk. One order-preserving filter: reconstruction replays
// these in window order.
export function artifactBackingOps(artifact, fileOps) {
  const flat = fileOps || [];
  const deploys = artifact.deploys || [];
  const strict = (o) => deploys.some((d) => d.path && d.path === o.path && d.sessionId === o.sessionId);
  const coveredPaths = new Set(flat.filter(strict).map((o) => o.path));
  const uncovered = new Set((artifact.paths || []).filter((p) => !coveredPaths.has(p)));
  return flat.filter((o) => strict(o) || uncovered.has(o.path));
}

// The deep walk: when the sampled window can't anchor an artifact's backing
// file (its authoring Write predates the sample, and compaction means later
// bodies don't re-carry it), fetch the deploy sessions' own prompts and extract
// both channels — file ops and shell ops — UNFILTERED. Two consumers share the
// one pass, so the sessions hydrate once: deepArtifactOps takes the
// backing-path ops, artifactGenerators takes the shell hop. IO is injected
// (fetchSessionRows, hydrate, extractOps, extractShell) so the walk's real
// logic — session cap/dedup, per-prompt attribution — is directly testable;
// the component wires the live query/hydrate.
//
// `skipped` reports deploy sessions the cap dropped: a "nothing found" verdict
// derived from half the sessions has to say so rather than read as exhaustive.
export async function walkArtifactSessions(artifact, io) {
  const { fetchSessionRows, hydrate, extractOps, extractShell, concurrency = 6, sessionCap = 4 } = io;
  const all = [...new Set((artifact.deploys || []).map((d) => d.sessionId).filter(Boolean))];
  const sessions = all.slice(0, sessionCap);
  const walk = { prompts: [], sessions, skipped: all.length - sessions.length };
  if (!sessions.length) return walk;
  const rowsToWalk = [];
  for (const sess of sessions) {
    for (const row of await fetchSessionRows(sess)) rowsToWalk.push({ row, sess });
  }
  let next = 0;
  async function worker() {
    while (next < rowsToWalk.length) {
      const { row, sess } = rowsToWalk[next++];
      try {
        const ev = await hydrate(row.id);
        if (!ev?.turns?.length) continue;
        const fileOps = extractOps ? extractOps(ev.turns) : [];
        const shellOps = extractShell ? extractShell(ev.turns) : [];
        if (!fileOps.length && !shellOps.length) continue;
        walk.prompts.push({
          promptId: row.id,
          sessionId: ev.session_id || row.sessionId || sess,
          endpointId: ev.endpoint_id || row.endpointId || "",
          endpointName: row.endpointName || "",
          ts: row.timestamp ?? ev.event_time ?? null,
          fileOps,
          shellOps,
        });
      } catch { /* skip a row that won't hydrate/parse */ }
    }
  }
  await Promise.all(Array.from({ length: Math.min(concurrency, Math.max(1, rowsToWalk.length)) }, () => worker()));
  return walk;
}

// The walked sessions' ops for the artifact's own backing paths, merged onto
// the window's (dedup by (session, tool_use id), chronological replay order).
export function artifactOpsFromWalk(artifact, windowOps, prompts) {
  const paths = new Set(artifact.paths || []);
  const walked = collectWindowOps((prompts || []).map((p) => ({
    ...p, ops: (p.fileOps || []).filter((o) => paths.has(o.path)),
  })));
  const merged = new Map();
  for (const op of [...(windowOps || []), ...walked]) {
    merged.set(`${op.sessionId}|${op.key}`, op);
  }
  return [...merged.values()].sort((a, b) => (tsOf(a.ts) ?? 0) - (tsOf(b.ts) ?? 0));
}

export async function deepArtifactOps(artifact, windowOps, io) {
  if (!(artifact.paths || []).length) return [...(windowOps || [])];
  const { prompts } = await walkArtifactSessions(artifact, io);
  return artifactOpsFromWalk(artifact, windowOps, prompts);
}

// The artifact's backing paths — every deploy's own path, else the single
// newest one. A URL-identified artifact republished from different files
// carries several, and every source has to follow all of them.
export function artifactPaths(artifact) {
  const paths = artifact?.paths || [];
  if (paths.length) return paths;
  return artifact?.path ? [artifact.path] : [];
}

// Every source for an artifact's bytes, tried in descending directness, with
// `via` naming the one that answered. The ORDER is the logic — the fs signal is
// an indexed per-path lookup immune to both sampling limits the session walk
// lives under, so asking it first also means a hit skips the walk's hydrations
// entirely. IO is injected (`fetchSignalEvents` alongside the walk's own
// fetch/hydrate) so the order is testable without a live query layer.
//
// `fetchSignalEvents` is deliberately PER PATH: enumerating an artifact's paths
// and merging their events belongs here (one collectWindowOps across all of
// them dedups and orders consistently), leaving the component with just the one
// query it can't lift out — the fs lookup is coupled to fsProvenance, which
// reads `window` at module eval and so stays out of this module.
export async function resolveArtifactSources(artifact, io) {
  const { fetchSignalEvents, windowOps } = io;
  const signalEvents = [];
  if (fetchSignalEvents) {
    for (const path of artifactPaths(artifact)) {
      signalEvents.push(...(await fetchSignalEvents(path) || []));
    }
  }
  const signalOps = collectWindowOps(signalEvents);
  if (signalOps.length) return { rec: reconstructFile(signalOps), via: "signal", generators: null };
  const walk = await walkArtifactSessions(artifact, io);
  const generators = {
    ...artifactGenerators(artifact, walk.prompts),
    walked: walk.sessions.length,
    skipped: walk.skipped,
  };
  const ops = artifactOpsFromWalk(artifact, windowOps || [], walk.prompts);
  if (ops.length) return { rec: reconstructFile(ops), via: "sessions", generators };
  if (generators.recovered.length) return { rec: reconstructFile(generators.recovered), via: "shell", generators };
  return { rec: null, via: null, generators };
}

// ── Reconstruction provenance ───────────────────────────────────────────────

// The ops that actually contributed lines to a reconstruction, read back off
// the rec — reconstructFile keeps each line's authoring op on its entry, so a
// consumer can state provenance without threading the op list alongside.
export function recSourceOps(rec) {
  const seen = new Set();
  const out = [];
  const take = (op) => {
    if (!op || seen.has(op)) return;
    seen.add(op);
    out.push(op);
  };
  for (const row of rec?.rows || []) {
    if (row.op) take(row.op);
    for (const op of row.entry?.touches || []) take(op);
    for (const v of row.entry?.versions || []) take(v.op);
  }
  return out;
}

// How a reconstruction was obtained. `via` names the SOURCE; the compaction
// tally is ORTHOGONAL to it — summarized ops ride along in any window or
// session reconstruction — and has to be stated either way, because bytes
// recovered from a compaction summary are a weaker claim than a captured write:
// the summary is the harness's own re-rendering of a call whose body it dropped.
const VIA_LABEL = {
  signal: "reconstructed from the fs signal",
  window: "reconstructed from file operations",
  sessions: "reconstructed from the deploy traces' file operations",
  shell: "recovered from a shell write",
};

export function describeArtifactSource(via, ops) {
  const base = VIA_LABEL[via] || VIA_LABEL.window;
  const list = ops || [];
  const sum = list.filter((o) => o?.summarized).length;
  if (!sum) return base;
  if (sum === list.length) return "recovered from compaction memory; no file operation was retained";
  return `${base} · ${sum} of ${list.length} ops recovered from compaction memory`;
}

// ── The shell channel ───────────────────────────────────────────────────────
//
// A file the shell wrote never appears as a Write/Edit tool_use, so file-op
// replay is blind to it — the biggest hole under a published artifact, since a
// generated report is usually rendered by a script rather than typed out. Two
// shapes stay recoverable from the command text alone:
//
//   pass-through + heredoc — `cat > page.html <<'EOF' … EOF`: the bytes ARE in
//                            the command, so a write op is synthesizable.
//   interpreter + script   — `python3 gen.py` where gen.py writes the page: the
//                            bytes live one hop away in the SCRIPT, which is
//                            itself usually a captured Write.
//
// Only `cat`/`tee`/`dd` heredocs yield content. `python3 <<'PY' > page.html`
// redirects a PROGRAM's stdout, so its heredoc body is the generator, not the
// page — that body is emitted as an inline `run` instead of as page content.
//
// Ops here are their own channel, NOT file ops: `{ ch: "write" | "run",
// target, content, script, command }`. artifactGenerators is what converts a
// content-bearing write into a real, reconstructable file op.
const PASSTHROUGH_RE = /(?:^|[\s;&|(])(?:cat|tee|dd)(?:\s|$)/;
const RUNNER_RE = /(?:^|[\s;&|(])(?:(?:\S*\/)?(?:python3?(?:\.\d+)?|node|deno|bun|bash|sh|zsh|ruby|perl|osascript)|uv\s+run|\.{0,2}\/\S+\.(?:py|js|mjs|sh|rb|pl))(?:\s|$)/;
const SCRIPT_TOKEN_RE = /[\w./~-]+\.(?:py|js|mjs|cjs|ts|sh|bash|zsh|rb|pl)\b/g;
const SCRIPT_EXT_RE = /\.(?:py|js|mjs|cjs|ts|sh|bash|zsh|rb|pl)$/i;
const HD_RE = /(?:^|\s)HD(\d+)(?:\s|$)/;
const SHELL_CMD_CAP = 2000;
const SHELL_OPS_PER_COMMAND_CAP = 8;

const countLines = (s) => (s === "" ? 0 : s.split("\n").length);

const baseOf = (p) => (p ? String(p).replace(/\\/g, "/").split("/").pop() : "");

// The token starting at `at`, honoring a quoted span (`> "my page.html"`).
function tokenAt(raw, at) {
  const c = raw[at];
  if (c === '"' || c === "'") {
    const end = raw.indexOf(c, at + 1);
    return end === -1 ? null : raw.slice(at + 1, end) || null;
  }
  const m = /^[^\s;|&<>()]+/.exec(raw.slice(at));
  return m ? m[0] : null;
}

// Redirect and `tee` targets of one statement. `mask` is the quote-blanked
// statement (same length as `raw`), so an operator inside a quoted payload
// can't register as a redirect while the target is still read from raw text.
function writeTargetsOf(raw, mask) {
  const out = [];
  for (let i = 0; i < mask.length; i++) {
    if (mask[i] !== ">") continue;
    const prev = mask[i - 1] || "";
    if (prev === "&" || /\d/.test(prev)) continue;  // 2>&1, 1>&2
    let j = mask[i + 1] === ">" ? i + 2 : i + 1;
    if (mask[j] === "&") continue;                  // >&2
    while (j < raw.length && (raw[j] === " " || raw[j] === "\t")) j += 1;
    const tok = tokenAt(raw, j);
    if (tok && !/^HD\d+$/.test(tok)) out.push(tok);
    i = j;
  }
  const tee = /(?:^|[\s;&|(])tee\s+(?:-a\s+)?/.exec(mask);
  if (tee) {
    const tok = tokenAt(raw, tee.index + tee[0].length);
    if (tok && !/^HD\d+$/.test(tok)) out.push(tok);
  }
  return out;
}

// The trailing text of each heredoc opener line, indexed like stripHeredocs'
// bodies — same opener grammar and the same body-skipping walk, so the indices
// align. stripHeredocs consumes that text, and `cat <<'EOF' > page.html` puts
// the redirect there, so without this the write is invisible.
function heredocTails(src) {
  const tails = [];
  const open = /<<-?\s*(["']?)([A-Za-z_][\w]*)\1([^\n]*)\n/g;
  let m;
  while ((m = open.exec(src))) {
    tails.push(m[3] || "");
    const rest = src.slice(open.lastIndex);
    const em = new RegExp(`^\\s*${m[2]}\\s*$`, "m").exec(rest);
    open.lastIndex = em ? open.lastIndex + em.index + em[0].length : src.length;
  }
  return tails;
}

// One shell command → its write/run channel ops.
export function shellChannelOps(cmd) {
  const src = String(cmd || "").replace(/\\\r?\n/g, " "); // as stripHeredocs sees it
  const { text, bodies } = stripHeredocs(src);
  const tails = heredocTails(src);
  const out = [];
  for (const stmt of splitStatements(text)) {
    const mask = unquoted(stmt);
    const hd = HD_RE.exec(mask);
    const n = hd ? Number(hd[1]) : -1;
    const body = n >= 0 ? bodies[n] ?? null : null;
    const tail = n >= 0 ? tails[n] || "" : "";
    const runner = RUNNER_RE.test(mask);
    const content = !runner && PASSTHROUGH_RE.test(mask) ? body : null;
    const targets = [...writeTargetsOf(stmt, mask), ...(tail ? writeTargetsOf(tail, unquoted(tail)) : [])];
    for (const target of new Set(targets)) out.push({ ch: "write", target, content });
    if (!runner) continue;
    const seen = new Set();
    for (const tok of stmt.match(SCRIPT_TOKEN_RE) || []) {
      if (seen.has(tok)) continue;
      seen.add(tok);
      out.push({ ch: "run", target: tok, script: null });
    }
    if (body && !seen.size) out.push({ ch: "run", target: "", script: body });
  }
  return out;
}

export function extractShellOps(turns) {
  const ops = [];
  if (!Array.isArray(turns)) return ops;
  for (const turn of turns) {
    for (const b of turn?.blocks || []) {
      const cmd = shellCommandOf(b);
      if (!cmd) continue;
      const parsed = shellChannelOps(cmd);
      if (!parsed.length) continue;
      const command = cmd.length > SHELL_CMD_CAP ? `${cmd.slice(0, SHELL_CMD_CAP)}…` : cmd;
      for (const p of parsed.slice(0, SHELL_OPS_PER_COMMAND_CAP)) {
        ops.push({
          ...p,
          key: b.id ? `${b.id}:${p.ch}:${p.target}` : `sh:${hashString(`${cmd}|${p.ch}|${p.target}`)}`,
          command,
        });
      }
    }
  }
  return ops;
}

// ── The generator hop ───────────────────────────────────────────────────────

// A basename counts as WRITTEN by some text only when it sits right after a
// file-opening call or a redirect — merely being mentioned (a grep, a log line)
// is not authorship.
const WRITE_MARKER_RE = /open\s*\(|\.write\s*\(|write_text\s*\(|writeFileSync\s*\(|writeFile\s*\(|createWriteStream\s*\(|savefig\s*\(|>>?\s*$/;
const MARKER_LOOKBEHIND = 60;
const BASE_SCAN_CAP = 50;
const GENERATOR_CAP = 3;

function opText(op) {
  if (typeof op.content === "string") return op.content;
  if (op.hunks?.length) return op.hunks.map((h) => h.new || "").join("\n");
  if (op.obs?.length) return op.obs.map((o) => o[1]).join("\n");
  return "";
}

function writesTarget(text, base) {
  let i = text.indexOf(base);
  for (let n = 0; i !== -1 && n < BASE_SCAN_CAP; n += 1) {
    if (WRITE_MARKER_RE.test(text.slice(Math.max(0, i - MARKER_LOOKBEHIND), i))) return true;
    i = text.indexOf(base, i + base.length);
  }
  return false;
}

// When nothing in the corpus wrote the artifact's OWN path, find what did:
//   recovered   — write ops synthesized from a shell heredoc that carried the
//                 artifact's bytes, re-pathed onto the artifact's own path.
//                 This IS the artifact: reconstructFile-ready, chronological.
//   scripts     — ranked generator candidates. `ops` reconstructs the SCRIPT,
//                 never the artifact; a consumer must label it as source.
//   shellWrites — evidence only: the shell wrote the artifact, but a program's
//                 stdout was redirected so the bytes aren't in the command.
// Redirect targets are usually BARE (`> page.html` under a `cd`), so the match
// is by basename and the synthesized op is re-pathed onto the artifact's path.
export function artifactGenerators(artifact, prompts) {
  const list = prompts || [];
  const paths = artifactPaths(artifact);
  const own = new Set(paths);
  const pathOfBase = new Map();
  for (const p of paths) if (!pathOfBase.has(baseOf(p))) pathOfBase.set(baseOf(p), p);
  const out = { recovered: [], scripts: [], shellWrites: [] };
  if (!pathOfBase.size) return out;

  const ranBase = new Map();
  const inlineRuns = [];
  for (const op of collectWindowOps(list.map((p) => ({ ...p, ops: p.shellOps || [] })))) {
    if (op.ch === "write") {
      const target = pathOfBase.get(baseOf(op.target));
      if (!target) continue;
      if (op.content) {
        out.recovered.push({
          key: op.key, kind: "write", path: target, ok: true,
          plus: countLines(op.content), minus: 0, readLines: 0,
          content: op.content, contentTruncated: false, viaShell: true,
          promptId: op.promptId, sessionId: op.sessionId,
          endpointId: op.endpointId, endpointName: op.endpointName, ts: op.ts,
        });
      } else {
        out.shellWrites.push(op);
      }
    } else if (op.ch === "run") {
      if (op.target) {
        const arr = ranBase.get(baseOf(op.target)) || [];
        arr.push(op);
        ranBase.set(baseOf(op.target), arr);
      } else if (op.script) {
        inlineRuns.push(op);
      }
    }
  }
  out.recovered.sort((a, b) => (tsOf(a.ts) ?? 0) - (tsOf(b.ts) ?? 0));

  const byPath = new Map();
  for (const op of collectWindowOps(list.map((p) => ({ ...p, ops: p.fileOps || [] })))) {
    if (!op.path || own.has(op.path)) continue;
    let c = byPath.get(op.path);
    if (!c) { c = { path: op.path, name: baseOf(op.path), ops: [], writes: 0, hit: false }; byPath.set(op.path, c); }
    c.ops.push(op);
    if (op.kind === "write" || op.kind === "edit") c.writes += 1;
    if (!c.hit) {
      const text = opText(op);
      if (text) for (const base of pathOfBase.keys()) if (writesTarget(text, base)) { c.hit = true; break; }
    }
  }
  // Writing the artifact's name is what ADMITS a candidate — an unrelated
  // script that merely ran in the same session can't place. It must also be
  // runnable: either it carries a script extension or we watched the shell
  // execute it. Without that second test a runbook that DOCUMENTS the
  // generator in a fenced code block scores as the generator.
  out.scripts = [...byPath.values()]
    .map((c) => ({ ...c, runs: ranBase.get(c.name) || [] }))
    .filter((c) => c.hit && (SCRIPT_EXT_RE.test(c.name) || c.runs.length))
    .map((c) => ({ ...c, score: 3 + (c.runs.length ? 2 : 0) + (c.writes ? 1 : 0) }))
    .sort((a, b) => b.score - a.score || b.writes - a.writes || a.name.localeCompare(b.name))
    .slice(0, GENERATOR_CAP);

  // An inline heredoc generator has no path of its own; present it as a script
  // whose single synthetic write reconstructs like any other. Filler only —
  // a snippet that patches the real generator also mentions the artifact under
  // a write marker, so these must never dilute a path-backed answer.
  for (const r of out.scripts.length ? [] : inlineRuns) {
    if (out.scripts.length >= GENERATOR_CAP) break;
    if (![...pathOfBase.keys()].some((b) => writesTarget(r.script, b))) continue;
    out.scripts.push({
      path: "", name: "(inline heredoc)", writes: 1, hit: true, score: 4, runs: [r],
      ops: [{
        key: `${r.key}:inline`, kind: "write", path: "", ok: true,
        plus: countLines(r.script), minus: 0, readLines: 0,
        content: r.script, contentTruncated: false,
        promptId: r.promptId, sessionId: r.sessionId, ts: r.ts,
      }],
    });
  }
  return out;
}

function str(v) {
  return typeof v === "string" && v ? v : null;
}

// djb2 — stable synthetic dedup key (same as the sibling libs).
function hashString(s) {
  let h = 5381;
  for (let i = 0; i < s.length; i++) h = ((h << 5) + h + s.charCodeAt(i)) | 0;
  return (h >>> 0).toString(36);
}

function filePathOf(input) {
  for (const k of ["file_path", "path", "notebook_path", "filePath"]) {
    const v = input?.[k];
    if (typeof v === "string" && v) return v;
  }
  return null;
}

function contentText(content) {
  if (typeof content === "string") return content;
  if (Array.isArray(content)) return content.map((p) => (typeof p === "string" ? p : p?.text || "")).join("\n");
  return "";
}

// One prompt's turns → { invocations, marks } in collectWindowSkillActivity's
// shape (so the collect/build pipeline is shared), but marks are ONLY publish
// marks. Skill invocations come through both doors (tool + typed slash) —
// they exist here purely to label artifacts.
export function extractArtifactSignals(turns) {
  const invocations = [];
  const marks = [];
  if (!Array.isArray(turns)) return { invocations, marks };
  const resultById = new Map();
  for (const turn of turns) {
    for (const b of turn?.blocks || []) {
      if (b?.type === "tool_result" && b.tool_use_id) resultById.set(b.tool_use_id, b);
    }
  }
  for (const turn of turns) {
    for (const b of turn?.blocks || []) {
      if (b?.type === "tool_use") {
        const last = String(b.name || "").split("__").pop().toLowerCase();
        const input = b.input && typeof b.input === "object" ? b.input : {};
        if (last === "skill" || last === "slashcommand") {
          const norm = normalizeSkillName(str(input.skill) || str(input.command) || str(input.name));
          if (norm) {
            invocations.push({
              ...norm,
              args: null, // labels don't need the brief
              source: "tool",
              key: b.id || `tool:${hashString(norm.name)}`,
            });
          }
        } else if (last === "artifact") {
          const path = filePathOf(input);
          const title = str(input.title) || (path ? path.split(/[\\/]/).pop() : null);
          if (!path && !title) continue;
          const resultText = b.id ? contentText(resultById.get(b.id)?.content) : "";
          const um = /https?:\/\/[^\s"'<>)\]]+/.exec(resultText || "");
          marks.push({
            key: b.id || `pub:${hashString((path || "") + (title || ""))}`,
            ch: "artifacts",
            publish: {
              title: title || null,
              description: str(input.description) || null,
              favicon: str(input.favicon) || null,
              path: path || null,
              url: um ? um[0] : null,
            },
          });
        }
      } else if (b?.type === "text" && turn?.role === "user") {
        // Typed slash invocations — text markers only (see traceSkillRuns).
        for (const inv of commandInvocationsOf(b.text)) {
          invocations.push({
            ...inv,
            key: `cmd:${hashString(inv.name + "|" + (inv.args || "").slice(0, 200))}`,
          });
        }
      }
    }
  }
  return { invocations, marks };
}

function tsOf(v) {
  if (v instanceof Date) return v.getTime();
  if (typeof v === "number") return v;
  const p = Date.parse(v);
  return Number.isNaN(p) ? null : p;
}

function shortId(id) {
  return id ? `${String(id).slice(0, 8)}…` : "unknown";
}

// The window's artifact activity, from collectWindowSkillActivity's output:
//   artifacts: [{ key, title, description, favicon, path, url, skill,
//                 deploys:[{ts, promptId, sessionId, endpointId,
//                 endpointName}], n, firstTs, lastTs, sessionIds, actors,
//                 publishers:[{id,name,n}] }]
//   actors:    [{ id, name, deploys, artifacts, sessions }]
//   totals:    { artifacts, deploys, actors, sessions, skills }
export function buildArtifactActivity({ runs, marks }) {
  // Skill attribution mirrors buildSkillActivity: a deploy belongs to the
  // latest non-builtin run at-or-before it in its session.
  const runsBySession = new Map();
  for (const r of runs || []) {
    if (r.builtin) continue;
    let arr = runsBySession.get(r.sessionId);
    if (!arr) { arr = []; runsBySession.set(r.sessionId, arr); }
    arr.push(r);
  }
  for (const arr of runsBySession.values()) {
    arr.sort((a, b) => (tsOf(a.ts) ?? 0) - (tsOf(b.ts) ?? 0));
  }
  const skillOf = (sessionId, ts) => {
    const arr = runsBySession.get(sessionId);
    if (!arr) return null;
    const mts = tsOf(ts) ?? 0;
    let owner = null;
    for (const r of arr) {
      if ((tsOf(r.ts) ?? 0) <= mts) owner = r;
      else break;
    }
    return owner ? owner.name : null;
  };

  const artifacts = new Map();
  const actors = new Map();
  for (const m of marks || []) {
    const p = m.publish;
    if (!p) continue;
    const key = p.url || `${m.sessionId}|${p.path || p.title || "?"}`;
    let a = artifacts.get(key);
    if (!a) {
      a = {
        key,
        title: null, description: null, favicon: null, path: p.path || null, url: null,
        deploys: [], sessionIds: new Set(), publishers: new Map(), skills: new Map(),
      };
      artifacts.set(key, a);
    }
    // Newest metadata wins — titles get edited between deploys.
    if (p.title) a.title = p.title;
    if (p.description) a.description = p.description;
    if (p.favicon) a.favicon = p.favicon;
    if (p.url) a.url = p.url;
    if (p.path) a.path = p.path;
    a.deploys.push({
      ts: m.ts ?? null, promptId: m.promptId, sessionId: m.sessionId,
      endpointId: m.endpointId, endpointName: m.endpointName,
      // Each deploy remembers ITS OWN backing path — a URL-identified artifact
      // can be republished from different files across sessions, and
      // reconstruction must follow every one of them, not just the newest.
      path: p.path || null,
    });
    a.sessionIds.add(m.sessionId);
    const pub = a.publishers.get(m.endpointId) || { id: m.endpointId, name: m.endpointName || shortId(m.endpointId), n: 0 };
    pub.n += 1;
    a.publishers.set(m.endpointId, pub);
    const skill = skillOf(m.sessionId, m.ts);
    if (skill) a.skills.set(skill, (a.skills.get(skill) || 0) + 1);

    let actor = actors.get(m.endpointId);
    if (!actor) {
      actor = { id: m.endpointId, name: m.endpointName || shortId(m.endpointId), deploys: 0, artifacts: new Set(), sessions: new Set() };
      actors.set(m.endpointId, actor);
    }
    actor.deploys += 1;
    actor.artifacts.add(key);
    actor.sessions.add(m.sessionId);
  }

  const artifactList = [...artifacts.values()].map((a) => {
    const deploys = [...a.deploys].sort((x, y) => (tsOf(x.ts) ?? 0) - (tsOf(y.ts) ?? 0));
    const topSkill = [...a.skills.entries()].sort((x, y) => y[1] - x[1])[0];
    return {
      key: a.key,
      title: a.title, description: a.description, favicon: a.favicon,
      path: a.path, url: a.url,
      paths: [...new Set(deploys.map((d) => d.path).filter(Boolean))],
      skill: topSkill ? topSkill[0] : null,
      deploys,
      n: deploys.length,
      firstTs: deploys[0]?.ts ?? null,
      lastTs: deploys[deploys.length - 1]?.ts ?? null,
      sessionIds: [...a.sessionIds],
      publishers: [...a.publishers.values()].sort((x, y) => y.n - x.n),
    };
  });
  // Most-shipped first, then recency — the tab is a feed of what got built.
  artifactList.sort((a, b) => (b.n - a.n) || ((tsOf(b.lastTs) ?? 0) - (tsOf(a.lastTs) ?? 0)) || String(a.title).localeCompare(String(b.title)));

  const actorList = [...actors.values()]
    .map((a) => ({ id: a.id, name: a.name, deploys: a.deploys, artifacts: a.artifacts.size, sessions: a.sessions.size }))
    .sort((a, b) => (b.deploys - a.deploys) || a.name.localeCompare(b.name));

  return {
    artifacts: artifactList,
    actors: actorList,
    totals: {
      artifacts: artifactList.length,
      deploys: artifactList.reduce((s, a) => s + a.n, 0),
      actors: actorList.length,
      sessions: new Set(artifactList.flatMap((a) => a.sessionIds)).size,
      skills: new Set(artifactList.map((a) => a.skill).filter(Boolean)).size,
    },
  };
}
